import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { RefreshCcw } from "lucide-react";

import CustomInput from "../../../components/UI/PageInput/CustomInput";
import CenteredHeading from "../../../components/UI/headingsUI/CenteredHeading";
import GrayButton from "../../../components/UI/buttonUI/GrayButton";
import SearchDropdown from "../../../components/UI/PageInput/SearchDropdown";
import CylinderDropdown from "../../../components/UI/PageInput/CylinderDropdown";
import usePostData from "../../../CustomHooks/useAxios/usePostData";
import { CHECK_CYLINDER, UPDATE_CYLINDER } from "../../../api/allApi_List";
import DialogBox from "../../../components/UI/DialogBox";
import { handleErrMessage } from "../../../functions/handleErrMessage";
import { prepareFormData } from "../../../functions/prepareFormData";
import CylinderUpdateSuccessView from "../dialog/CylinderUpdateSuccessView";
import useDebounce from "../../../CustomHooks/useDebounce";
import PrimaryButton from "../../../components/UI/buttonUI/PrimaryButton";
import { noSpaceOnlyValidation } from "../../../validations/formValidations";

const sizeOptions = [
  { id: 100, name: "100kg" },
  { id: 900, name: "900kg" },
];

const UpdateCylinderForm = ({ setupData }) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
    reset,
  } = useForm();

  const {
    executePost: updateCylinder,
    loading: updateLoading,
    formError: updateError,
    successResponse: updateResponse,
    setFormError: setUpdateFormError,
  } = usePostData();

  const {
    executePost: checkSerialNumber,
    loading: checkSerialLoading,
    formError: checkSerialErr,
    successResponse: serialNumberExist,
    setSuccessResponse: setSerialNumberExist,
    setFormError: setCheckSerialErr,
  } = usePostData();

  const userId = localStorage.getItem("userId");
  const newSerialNumber = watch("new_serial_number");
  const debouncedQuery = useDebounce(newSerialNumber, 500);

  useEffect(() => {
    if (debouncedQuery) {
      const formData = new FormData();
      formData.append("serial_number", newSerialNumber);
      checkSerialNumber(formData, CHECK_CYLINDER);
    }
    if (debouncedQuery === "") {
      setSerialNumberExist(null);
    }
    setSerialNumberExist(null);
  }, [debouncedQuery]);

  const handleClear = () => {
    setSerialNumberExist(null);
    setCheckSerialErr("");
    reset();
  };

  const onSubmit = (data) => {
    if (checkSerialLoading) return;

    if (newSerialNumber && ["true", "discarded"].includes(serialNumberExist)) {
      return;
    }

    const formData = prepareFormData(data);
    updateCylinder(formData, UPDATE_CYLINDER, handleClear);
  };

  return (
    <>
      <div className="form-container">
        <CenteredHeading title="Update Cylinder" />
        <form
          className="form-border max-w-3xl mx-auto w-95%"
          onSubmit={
            !updateLoading ? (e) => handleSubmit(onSubmit)(e) : undefined
          }
        >
          <CylinderDropdown
            label="Cylinder No.*"
            name={`id`}
            options={setupData.cylinders}
            setValue={setValue}
            control={control}
            error={errors.id}
          />
          <div className="flex gap-2">
            <div className="w-full">
              {" "}
              <CustomInput
                type="text"
                name="new_serial_number"
                labelText="New Cylinder No."
                placeholder="Enter"
                register={{
                  ...register("new_serial_number", {
                    ...noSpaceOnlyValidation,
                  }),
                }}
                error={errors.new_serial_number}
              />
              {/*=====================Cylinder Checking Error Messages===============*/}
              {checkSerialLoading && !checkSerialErr ? (
                <p className={`${cyErrorStyles.warning}`}>
                  Checking cylinder no.
                </p>
              ) : checkSerialErr ? (
                <p className={`${cyErrorStyles.error}`}>{checkSerialErr}</p>
              ) : serialNumberExist === "true" && !errors.new_serial_number ? (
                <p className={`${cyErrorStyles.error}`}>
                  This cylinder number already exists!
                </p>
              ) : serialNumberExist === "discarded" &&
                !errors.new_serial_number ? (
                <p className={`${cyErrorStyles.error}`}>
                  This cylinder number already exists and discarded!
                </p>
              ) : null}
              {/*===========================================================================*/}
            </div>
            <div className="w-full">
              <SearchDropdown
                name="new_size"
                label="Size"
                keyName="name"
                id="id"
                options={sizeOptions}
                setValue={setValue}
                control={control}
                selectedVal={watch("new_size")}
                error={errors.new_size}
                disableValidationMessage={true}
              />
            </div>
          </div>
          {userId === "1" && (
            <SearchDropdown
              name="new_station_id"
              label="Station"
              keyName="name"
              id="id"
              options={setupData.stations}
              setValue={setValue}
              control={control}
              selectedVal={watch("new_station_id")}
              error={errors.new_station_id}
              disableValidationMessage={true}
              showOptionId={true}
            />
          )}
          <div className="submitWrap">
            <p className="addWrap">
              <PrimaryButton
                btnText={!updateLoading ? "Update Cylinder" : "Updating..."}
                loading={updateLoading}
                Icon={RefreshCcw}
                size={18}
              />
            </p>
            <p className="grayWrap">
              <GrayButton btnText="Clear" width="w-40" onClick={handleClear} />
            </p>
          </div>
        </form>
      </div>
      {updateError && (
        <DialogBox
          title="Error"
          btnText="Ok"
          message={handleErrMessage(updateError)}
          closeAction={() => setUpdateFormError("")}
        />
      )}
      {updateResponse && (
        <CylinderUpdateSuccessView viewDetails={updateResponse} />
      )}
    </>
  );
};
export default UpdateCylinderForm;

const cyErrorStyles = {
  warning: "text-orange font-figtree text-xs -mt-[14px]",
  error: "text-red-500 font-figtree text-xs -mt-[14px]",
};
